import React from 'react';
import { colors } from '../../../../../../../packages/brand';
import { Box, Inline } from '../../../../../../../packages/ui';
import {
  BodyText,
  Button,
  HeaderText,
  Stack,
  TextFormField,
} from '../../../../ui';
import Image from '../../../../shared/Image';
import { isValidEmail } from '../../../../../modules/validators';

const Logo = require('../../../../../../images/styleseat-logo-black.svg');

export type CollectClientContactInfoStepProps = {
  loading: boolean;
  onSubmit: (emailAddress: string | null) => void;
};

const isEmailValid = (email: any): email is string => {
  if (typeof email !== 'string') return false;
  const trimmedEmail = email.trim();
  if (trimmedEmail.length === 0) return false;
  return isValidEmail(trimmedEmail);
};

export const CollectClientContactInfoStep: React.FunctionComponent<
CollectClientContactInfoStepProps
> = ({ loading, onSubmit }) => {
  const [email, setEmail] = React.useState<string>('');
  const [localError, setLocalError] = React.useState<string | null>(null);

  const handleSubmit = React.useCallback((inputEmail: any) => {
    if (loading) return;
    if (!isEmailValid(inputEmail)) {
      setLocalError('Please enter a valid email address');
      return;
    }
    setLocalError(null);
    onSubmit(inputEmail.trim());
  }, [
    loading,
    setLocalError,
    onSubmit,
  ]);

  const handleNoReceipt = React.useCallback(() => {
    setLocalError(null);
    onSubmit(null);
  }, [
    setLocalError,
    onSubmit,
  ]);

  return (
    <Stack flex={1} alignItems="stretch" testID="collect-client-contact-info">
      <Box>
        <Stack
          flex={1}
          space={20}
          alignItems="center"
          paddingY={16}
          borderBottomColor={colors.lightGrey}
          borderBottomWidth={1}
          borderStyle="solid"
        >
          <Image
            source={Logo}
            style={{
              width: 72,
              height: 38,
            }}
          />
          <Stack paddingX={24} paddingTop={12} alignItems="stretch">
            <Stack alignItems="center" space={16} paddingBottom={24}>
              <HeaderText level={2} testID="collect-client-contact-info__header-text">
                Would you like a receipt?
              </HeaderText>
              <BodyText textAlign="center" testID="collect-client-contact-info__body-text">
                Enter your email below to receive a receipt. Your information is protected
                and will not be sold to 3rd parties.
              </BodyText>
            </Stack>
            <Inline space={12} alignItems="flex-start">
              <Box flexGrow={2}>
                <TextFormField
                  autoCapitalize="none"
                  autoCorrect={false}
                  spellCheck={false}
                  type="email"
                  accentColor={colors.red}
                  message={localError}
                  autoFocus
                  label="Email"
                  value={email}
                  onChangeText={setEmail}
                  padding={0}
                  testID="collect-client-contact-info__input"
                  messageTestID="collect-client-contact-info__input-message"
                />
              </Box>
              <Box
                // @ts-expect-error Grandfathered
                paddingTop={22}
                flexGrow={1}
                maxWidth={70}
                minHeight={45}
              >
                <Button
                  appearance="primary-neue"
                  size="large"
                  loading={loading}
                  onClick={() => handleSubmit(email)}
                  testID="collect-client-contact-info__email-button"
                  style={{
                    minHeight: 45,
                  }}
                >
                  { !loading && 'Email' }
                </Button>
              </Box>
            </Inline>
            <Box paddingTop={40} alignItems="center">
              <Button
                appearance="link-lowercase"
                onClick={handleNoReceipt}
                size="medium"
                testID="collect-client-contact-info__no-receipt-button"
              >
                No receipt
              </Button>
            </Box>
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
};
